import React, { useCallback, useRef, useState } from 'react'
import styled from 'styled-components'
import { ConfirmContext } from 'sutra/components/confirm/confirm-context'
import type {
  ConfirmFn,
  ConfirmOptions,
} from 'sutra/components/confirm/confirm-types'
import { PopoverModal } from 'sutra/components/PopoverModal/PopoverModal'

const DEFAULT_WIDTH = 416

interface ActiveConfirm {
  options: ConfirmOptions
  isOpen: boolean
}

const TitleRow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

export const ConfirmProvider = ({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement => {
  // One slot, kept after it closes: PopoverModal needs the element mounted
  // through its exit animation, and the next `confirm()` simply replaces it.
  const [active, setActive] = useState<ActiveConfirm | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const resolveRef = useRef<((confirmed: boolean) => void) | null>(null)

  const settle = (confirmed: boolean): void => {
    resolveRef.current?.(confirmed)
    resolveRef.current = null
    setActive((current) => current && { ...current, isOpen: false })
  }

  const confirm = useCallback<ConfirmFn>(
    (options) =>
      new Promise<boolean>((resolve) => {
        // A confirm opened while another is showing supersedes it.
        resolveRef.current?.(false)
        resolveRef.current = resolve
        setIsLoading(false)
        setActive({ options, isOpen: true })
      }),
    [],
  )

  const handleOk = async (): Promise<void> => {
    if (!active?.options.onOk) {
      settle(true)
      return
    }
    setIsLoading(true)
    try {
      await active.options.onOk()
    } catch {
      // The caller's onOk owns error reporting; a rejection just keeps the
      // dialog open so the user can retry or cancel.
      setIsLoading(false)
      return
    }
    setIsLoading(false)
    settle(true)
  }

  const handleCancel = (): void => {
    active?.options.onCancel?.()
    settle(false)
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {active && (
        <PopoverModal
          isOpen={active.isOpen}
          onClose={handleCancel}
          showClose={false}
          disablePointerDismissal
          disableEscapeDismissal={isLoading}
          width={active.options.width ?? DEFAULT_WIDTH}
          zIndex={active.options.zIndex}
          title={
            <TitleRow>
              {active.options.icon}
              {active.options.title}
            </TitleRow>
          }
          content={active.options.content}
          okText={active.options.okText}
          cancelText={active.options.cancelText}
          confirmLoading={isLoading}
          okButtonProps={{ danger: active.options.danger }}
          cancelButtonProps={{ disabled: isLoading }}
          onOk={handleOk}
        />
      )}
    </ConfirmContext.Provider>
  )
}
